import Button from '../ui/Button'
import { useLang } from '../../contexts/LangContext'
import { localized } from '../../lib/i18n'
import type { CustomerProperty } from '../../graphql/customer'

const propertyTypeLabels = {
  residential: { el: 'Κατοικία', en: 'Residential' },
  office: { el: 'Γραφείο', en: 'Office' },
  airbnb: { el: 'Airbnb', en: 'Airbnb' },
  other: { el: 'Άλλο', en: 'Other' },
} as const

function propertyTypeLabel(propertyType: string, locale: 'el' | 'en') {
  const label = propertyTypeLabels[propertyType as keyof typeof propertyTypeLabels]
  return label ? label[locale] : propertyType
}

export default function PropertyCard({
  property,
  onEdit,
  onDelete,
  deleting,
}: {
  property: CustomerProperty
  onEdit: () => void
  onDelete: () => void
  deleting?: boolean
}) {
  const { locale, t } = useLang()
  const details = [
    property.sizeSqm ? `${property.sizeSqm} ${t('τ.μ.', 'sqm')}` : null,
    property.floor !== null ? `${t('Όροφος', 'Floor')} ${property.floor}` : null,
    property.bedrooms !== null ? `${property.bedrooms} ${t('Υ/Δ', 'beds')}` : null,
    property.bathrooms !== null ? `${property.bathrooms} ${t('Μπάνια', 'baths')}` : null,
    property.hasElevator ? t('Ασανσέρ', 'Elevator') : t('Χωρίς ασανσέρ', 'No elevator'),
  ].filter(Boolean)

  return (
    <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-bold text-slate-950">{property.label}</h2>
            {property.isDefault && (
              <span className="rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary-700">
                {t('Προεπιλογή', 'Default')}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm font-medium text-slate-500">{propertyTypeLabel(property.propertyType, locale)}</p>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            {[property.addressLine1, property.addressLine2, property.city, property.postalCode].filter(Boolean).join(', ')}
          </p>
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={onEdit}>
            {t('Επεξεργασία', 'Edit')}
          </Button>
          <Button type="button" variant="danger" loading={deleting} onClick={onDelete}>
            {t('Διαγραφή', 'Delete')}
          </Button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {details.map((detail) => (
          <span key={detail} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
            {detail}
          </span>
        ))}
      </div>

      {(property.accessInstructions || property.notes || property.preferences.length > 0) && (
        <div className="mt-5 rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
          {property.accessInstructions && (
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-950">{t('Πρόσβαση:', 'Access:')}</span> {property.accessInstructions}
            </p>
          )}
          {property.notes && (
            <p className="mt-2 text-sm text-slate-600">
              <span className="font-semibold text-slate-950">{t('Σημειώσεις:', 'Notes:')}</span> {property.notes}
            </p>
          )}
          {property.preferences.length > 0 && (
            <ul className="mt-3 space-y-2">
              {property.preferences.map((preference) => (
                <li key={preference.id} className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-950">{localized(preference.serviceType, locale)}</span>
                  {preference.specialInstructions && ` · ${preference.specialInstructions}`}
                  {preference.hasPets && ` · ${t('Κατοικίδια', 'Pets')}`}
                  {preference.suppliesProvided && ` · ${t('Παρέχονται υλικά', 'Supplies provided')}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  )
}
